<template>
  <div class="permisos-modulos-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="fas fa-users-cog me-2"></i>
          Permisos por Empresa
        </h1>
        <p class="page-subtitle">
          <i class="fas fa-building me-1"></i>
          Gestiona qué módulos activos están disponibles para cada empresa
        </p>
      </div>
      <div class="header-actions">
        <button @click="refreshData" class="btn-secondary-custom me-2">
          <i class="fas fa-sync-alt"></i>
          Actualizar
        </button>
        <button @click="showBulkAssignModal = true" class="btn-primary-custom">
          <i class="fas fa-layer-group"></i>
          Asignación Masiva
        </button>
      </div>
    </div>

    <!-- Filtros y Selección de Empresa -->
    <div class="filters-section">
      <div class="company-selector">
        <label class="form-label">Empresa:</label>
        <select v-model="selectedCompanyId" @change="loadCompanyModules" class="form-select company-select">
          <option value="">Seleccionar empresa...</option>
          <option v-for="company in companies" :key="company.id" :value="company.id">
            {{ company.business_name }} ({{ company.ruc }})
          </option>
        </select>
      </div>
      
      <div class="filter-controls">
        <div class="search-box">
          <i class="fas fa-search search-icon"></i>
          <input
            v-model="filters.search"
            @input="debouncedSearch"
            type="text"
            placeholder="Buscar módulos..."
            class="search-input"
          >
        </div>
        <select v-model="filters.moduleType" @change="applyFilters" class="form-select">
          <option value="">Todos los tipos</option>
          <option value="core">Solo Core</option>
          <option value="custom">Solo Personalizados</option>
        </select>
        <select v-model="filters.assignmentStatus" @change="applyFilters" class="form-select">
          <option value="">Todos los estados</option>
          <option value="assigned">Solo Asignados</option>
          <option value="not_assigned">Solo No Asignados</option>
        </select>
      </div>
    </div>

    <!-- Empresa Seleccionada Info -->
    <div v-if="selectedCompany" class="company-info-card">
      <div class="company-details">
        <div class="company-header">
          <h3>{{ selectedCompany.business_name }}</h3>
          <div class="company-badges">
            <span class="badge bg-info">{{ selectedCompany.ruc }}</span>
            <span class="badge" :class="getCompanyStatusBadge(selectedCompany.status)">
              {{ selectedCompany.status }}
            </span>
          </div>
        </div>
        <div class="company-stats">
          <div class="stat-item">
            <span class="stat-number">{{ companyStats.total_modules }}</span>
            <span class="stat-label">Módulos Disponibles</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ companyStats.assigned_modules }}</span>
            <span class="stat-label">Módulos Asignados</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ companyStats.core_assigned }}</span>
            <span class="stat-label">Core Asignados</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ companyStats.custom_assigned }}</span>
            <span class="stat-label">Personalizados Asignados</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2">{{ loadingMessage }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
      <button @click="refreshData" class="btn btn-outline-danger btn-sm ms-2">
        <i class="fas fa-sync-alt me-1"></i>
        Reintentar
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="!selectedCompanyId" class="empty-state">
      <i class="fas fa-building empty-icon"></i>
      <h3>Selecciona una Empresa</h3>
      <p>Elige una empresa de la lista para gestionar sus permisos de módulos</p>
    </div>

    <!-- Content -->
    <div v-else-if="selectedCompany" class="content-section">
      <!-- Módulos Grid -->
      <div v-if="filteredModules.length === 0" class="empty-state">
        <i class="fas fa-filter empty-icon"></i>
        <h3>No hay módulos</h3>
        <p>No se encontraron módulos con los filtros aplicados</p>
      </div>

      <div v-else class="modules-permissions-grid">
        <div v-for="group in groupedModules" :key="group.key" class="module-group">
          <div v-if="group.parent" class="module-permission-card"
            :class="{ 
              'module-assigned': group.parent.is_assigned,
              'module-core': group.parent.is_core,
              'module-updating': updating === group.parent.id
            }"
          >
            <div class="module-header">
              <div class="module-info">
                <h4 class="module-title">{{ group.parent.name }}</h4>
                <p class="module-code">{{ group.parent.code }}</p>
              </div>
              <div class="module-toggle">
                <div class="form-check form-switch">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="`permission-${group.parent.id}`"
                    v-model="group.parent.is_assigned"
                    @change="toggleModulePermission(group.parent)"
                    :disabled="updating === group.parent.id || !group.parent.is_active"
                  >
                  <label class="form-check-label" :for="`permission-${group.parent.id}`">
                    {{ group.parent.is_assigned ? 'Asignado' : 'No Asignado' }}
                  </label>
                </div>
              </div>
            </div>
            
            <div class="module-body">
              <p class="module-description">{{ group.parent.description || 'Sin descripción' }}</p>
              
              <div class="module-badges">
                <span class="badge" :class="{
                  'bg-danger': group.parent.is_core,
                  'bg-secondary': !group.parent.is_core
                }">
                  {{ group.parent.is_core ? 'Core' : 'Personalizado' }}
                </span>
                
                <span class="badge" :class="{
                  'bg-success': group.parent.is_active,
                  'bg-warning': !group.parent.is_active
                }">
                  {{ group.parent.is_active ? 'Activo' : 'Inactivo' }}
                </span>

                <span v-if="group.parent.is_assigned" class="badge bg-primary">
                  Asignado
                </span>
              </div>

              <div v-if="!group.parent.is_active" class="module-warning">
                <i class="fas fa-exclamation-triangle"></i>
                Este módulo está inactivo y no puede ser asignado
              </div>
            </div>
            
            <div class="module-footer">
              <small class="text-muted">
                <i class="fas fa-calendar me-1"></i>
                {{ group.parent.assigned_at ? `Asignado: ${formatDate(group.parent.assigned_at)}` : 'No asignado' }}
              </small>
            </div>
            
            <div v-if="updating === group.parent.id" class="module-loading">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Actualizando...</span>
              </div>
            </div>
          </div>

          <div v-else class="module-group-title">Otros módulos</div>

          <div class="module-children">
            <div 
              v-for="child in group.children" 
              :key="child.id" 
              class="module-permission-card module-child"
              :class="{ 
                'module-assigned': child.is_assigned,
                'module-core': child.is_core,
                'module-updating': updating === child.id
              }"
            >
              <div class="module-header">
                <div class="module-info">
                  <h4 class="module-title">{{ child.name }}</h4>
                  <p class="module-code">{{ child.code }}</p>
                </div>
                <div class="module-toggle">
                  <div class="form-check form-switch">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      :id="`permission-${child.id}`"
                      v-model="child.is_assigned"
                      @change="toggleModulePermission(child)"
                      :disabled="updating === child.id || !child.is_active"
                    >
                    <label class="form-check-label" :for="`permission-${child.id}`">
                      {{ child.is_assigned ? 'Asignado' : 'No Asignado' }}
                    </label>
                  </div>
                </div>
              </div>
              
              <div class="module-body">
                <p class="module-description">{{ child.description || 'Sin descripción' }}</p>
                
                <div class="module-badges">
                  <span class="badge" :class="{
                    'bg-danger': child.is_core,
                    'bg-secondary': !child.is_core
                  }">
                    {{ child.is_core ? 'Core' : 'Personalizado' }}
                  </span>
                  
                  <span class="badge" :class="{
                    'bg-success': child.is_active,
                    'bg-warning': !child.is_active
                  }">
                    {{ child.is_active ? 'Activo' : 'Inactivo' }}
                  </span>

                  <span v-if="child.is_assigned" class="badge bg-primary">
                    Asignado
                  </span>
                </div>

                <div v-if="!child.is_active" class="module-warning">
                  <i class="fas fa-exclamation-triangle"></i>
                  Este módulo está inactivo y no puede ser asignado
                </div>
              </div>
              
              <div class="module-footer">
                <small class="text-muted">
                  <i class="fas fa-calendar me-1"></i>
                  {{ child.assigned_at ? `Asignado: ${formatDate(child.assigned_at)}` : 'No asignado' }}
                </small>
              </div>
              
              <div v-if="updating === child.id" class="module-loading">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Actualizando...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Asignación Masiva -->
    <div class="modal fade" :class="{ show: showBulkAssignModal }" style="display: block;" v-if="showBulkAssignModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Asignación Masiva de Módulos</h5>
            <button type="button" class="btn-close" @click="showBulkAssignModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="bulk-assign-section">
              <h6>Plantillas Predefinidas</h6>
              <div class="template-buttons">
                <button @click="applyTemplate('basic')" class="btn btn-outline-secondary me-2">
                  Básico (Solo Core)
                </button>
                <button @click="applyTemplate('standard')" class="btn btn-outline-primary me-2">
                  Estándar (Core + Básicos)
                </button>
                <button @click="applyTemplate('premium')" class="btn btn-outline-success me-2">
                  Premium (Todos)
                </button>
                <button @click="applyTemplate('clear')" class="btn btn-outline-danger">
                  Limpiar Todo
                </button>
              </div>
            </div>

            <div class="bulk-assign-section mt-4">
              <h6>Copiar desde otra Empresa</h6>
              <div class="d-flex gap-2">
                <select v-model="copyFromCompanyId" class="form-select">
                  <option value="">Seleccionar empresa origen...</option>
                  <option 
                    v-for="company in companies" 
                    :key="company.id" 
                    :value="company.id"
                    :disabled="company.id === selectedCompanyId"
                  >
                    {{ company.business_name }}
                  </option>
                </select>
                <button 
                  @click="copyPermissionsFromCompany" 
                  :disabled="!copyFromCompanyId"
                  class="btn btn-outline-info"
                >
                  Copiar Permisos
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showBulkAssignModal = false">
              Cancelar
            </button>
            <button type="button" class="btn btn-primary" @click="saveBulkChanges">
              Aplicar Cambios
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showBulkAssignModal"></div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import api from '@/services/api'

export default {
  name: 'ModulosPermisos',
  setup() {
    
    // Estado reactivo
    const companies = ref([])
    const modules = ref([])
    const selectedCompanyId = ref('')
    const selectedCompany = ref(null)
    const loading = ref(false)
    const loadingMessage = ref('Cargando...')
    const error = ref('')
    const updating = ref(null)
    const showBulkAssignModal = ref(false)
    const copyFromCompanyId = ref('')
    
    // Filtros
    const filters = ref({
      search: '',
      moduleType: '',
      assignmentStatus: ''
    })

    // Estadísticas de la empresa
    const companyStats = ref({
      total_modules: 0,
      assigned_modules: 0,
      core_assigned: 0,
      custom_assigned: 0
    })

    // Debounced search
    let searchTimeout = null
    const debouncedSearch = () => {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(applyFilters, 300)
    }

    // Computed properties
    const filteredModules = computed(() => {
      let filtered = modules.value

      // Filtro por búsqueda
      if (filters.value.search) {
        const search = filters.value.search.toLowerCase()
        filtered = filtered.filter(module => 
          module.name.toLowerCase().includes(search) ||
          module.code.toLowerCase().includes(search) ||
          (module.description && module.description.toLowerCase().includes(search))
        )
      }

      // Filtro por tipo de módulo
      if (filters.value.moduleType) {
        if (filters.value.moduleType === 'core') {
          filtered = filtered.filter(module => module.is_core)
        } else if (filters.value.moduleType === 'custom') {
          filtered = filtered.filter(module => !module.is_core)
        }
      }

      // Filtro por estado de asignación
      if (filters.value.assignmentStatus) {
        if (filters.value.assignmentStatus === 'assigned') {
          filtered = filtered.filter(module => module.is_assigned)
        } else if (filters.value.assignmentStatus === 'not_assigned') {
          filtered = filtered.filter(module => !module.is_assigned)
        }
      }

      return filtered
    })

    const groupedModules = computed(() => {
      const list = [...filteredModules.value]
      const parents = list.filter(m => !String(m.code).includes('.'))
      const usedIds = new Set()
      const groups = []

      const sortByCode = (a, b) => String(a.code).localeCompare(String(b.code), 'es', { numeric: true })

      parents.sort(sortByCode).forEach(parent => {
        usedIds.add(parent.id)
        const prefix = `${parent.code}.`
        const children = list
          .filter(m => m.code && String(m.code).startsWith(prefix))
          .sort(sortByCode)
        children.forEach(child => usedIds.add(child.id))
        groups.push({ key: `parent-${parent.id}`, parent, children })
      })

      const orphans = list.filter(m => !usedIds.has(m.id)).sort(sortByCode)
      if (orphans.length) {
        groups.push({ key: 'orphans', parent: null, children: orphans })
      }

      return groups
    })

    // Métodos
    const refreshData = async () => {
      await loadCompanies()
      if (selectedCompanyId.value) {
        await loadCompanyModules()
      }
    }

    const loadCompanies = async () => {
      try {
        loading.value = true
        loadingMessage.value = 'Cargando empresas...'
        
        const response = await api.get('/companies?per_page=100')
        
        if (response.data.success) {
          companies.value = response.data.data || []
        } else {
          throw new Error(response.data.message || 'Error al cargar empresas')
        }
      } catch (err) {
        console.error('Error loading companies:', err)
        error.value = err.response?.data?.message || err.message || 'Error al cargar empresas'
      } finally {
        loading.value = false
      }
    }

    const loadCompanyModules = async () => {
      if (!selectedCompanyId.value) return
      
      try {
        loading.value = true
        loadingMessage.value = 'Cargando módulos de la empresa...'
        
        // Encontrar la empresa seleccionada
        selectedCompany.value = companies.value.find(c => c.id == selectedCompanyId.value)
        
        // Cargar módulos con permisos de la empresa
        const response = await api.get(`/companies/${selectedCompanyId.value}/modules`)
        
        if (response.data.success) {
          modules.value = response.data.data || []
          calculateStats()
        } else {
          throw new Error(response.data.message || 'Error al cargar módulos')
        }
      } catch (err) {
        console.error('Error loading company modules:', err)
        error.value = err.response?.data?.message || err.message || 'Error al cargar módulos'
      } finally {
        loading.value = false
      }
    }

    const calculateStats = () => {
      const totalModules = modules.value.length
      const assignedModules = modules.value.filter(m => m.is_assigned).length
      const coreAssigned = modules.value.filter(m => m.is_core && m.is_assigned).length
      const customAssigned = modules.value.filter(m => !m.is_core && m.is_assigned).length
      
      companyStats.value = {
        total_modules: totalModules,
        assigned_modules: assignedModules,
        core_assigned: coreAssigned,
        custom_assigned: customAssigned
      }
    }

    const toggleModulePermission = async (module) => {
      if (!module.is_active) {
        alert('No se puede asignar un módulo inactivo')
        module.is_assigned = false
        return
      }

      try {
        updating.value = module.id
        
        const response = await api.post(`/companies/${selectedCompanyId.value}/modules/${module.id}/toggle`, {
          assigned: module.is_assigned
        })
        
        if (response.data.success) {
          if (module.is_assigned) {
            module.assigned_at = new Date().toISOString()
          } else {
            module.assigned_at = null
          }
          calculateStats()
        } else {
          // Revertir el cambio si falla
          module.is_assigned = !module.is_assigned
          throw new Error(response.data.message || 'Error al actualizar permisos')
        }
      } catch (err) {
        console.error('Error toggling module permission:', err)
        alert('Error al actualizar permisos: ' + (err.response?.data?.message || err.message))
        // Revertir el cambio
        module.is_assigned = !module.is_assigned
      } finally {
        updating.value = null
      }
    }

    const applyFilters = () => {
      // Los filtros se aplican automáticamente a través del computed
    }

    const getCompanyStatusBadge = (status) => {
      const badges = {
        'active': 'bg-success',
        'inactive': 'bg-danger',
        'pending': 'bg-warning',
        'suspended': 'bg-secondary'
      }
      return badges[status] || 'bg-secondary'
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    // Métodos para asignación masiva
    const applyTemplate = async (template) => {
      if (!selectedCompanyId.value) return
      
      try {
        loading.value = true
        loadingMessage.value = 'Aplicando plantilla...'
        
        let moduleIds = []
        
        switch (template) {
          case 'basic':
            moduleIds = modules.value.filter(m => m.is_core && m.is_active).map(m => m.id)
            break
          case 'standard':
            moduleIds = modules.value
              .filter(m => (m.is_core || ['clientes', 'productos', 'facturas'].includes(m.code)) && m.is_active)
              .map(m => m.id)
            break
          case 'premium':
            moduleIds = modules.value.filter(m => m.is_active).map(m => m.id)
            break
          case 'clear':
            moduleIds = []
            break
        }
        
        const response = await api.post(`/companies/${selectedCompanyId.value}/modules/bulk-assign`, {
          module_ids: moduleIds
        })
        
        if (response.data.success) {
          await loadCompanyModules()
          showBulkAssignModal.value = false
          alert('Plantilla aplicada correctamente')
        } else {
          throw new Error(response.data.message || 'Error al aplicar plantilla')
        }
      } catch (err) {
        console.error('Error applying template:', err)
        alert('Error al aplicar plantilla: ' + (err.response?.data?.message || err.message))
      } finally {
        loading.value = false
      }
    }

    const copyPermissionsFromCompany = async () => {
      if (!copyFromCompanyId.value || !selectedCompanyId.value) return
      
      try {
        loading.value = true
        loadingMessage.value = 'Copiando permisos...'
        
        const response = await api.post(`/companies/${selectedCompanyId.value}/modules/copy-from`, {
          source_company_id: copyFromCompanyId.value
        })
        
        if (response.data.success) {
          await loadCompanyModules()
          showBulkAssignModal.value = false
          copyFromCompanyId.value = ''
          alert('Permisos copiados correctamente')
        } else {
          throw new Error(response.data.message || 'Error al copiar permisos')
        }
      } catch (err) {
        console.error('Error copying permissions:', err)
        alert('Error al copiar permisos: ' + (err.response?.data?.message || err.message))
      } finally {
        loading.value = false
      }
    }

    const saveBulkChanges = () => {
      // Este método se puede usar para confirmar cambios masivos si es necesario
      showBulkAssignModal.value = false
    }

    // Watchers
    watch(selectedCompanyId, (newId) => {
      if (newId) {
        loadCompanyModules()
      } else {
        selectedCompany.value = null
        modules.value = []
      }
    })

    // Lifecycle
    onMounted(async () => {
      await loadCompanies()
    })

    return {
      // Estado
      companies,
      modules,
      selectedCompanyId,
      selectedCompany,
      loading,
      loadingMessage,
      error,
      updating,
      showBulkAssignModal,
      copyFromCompanyId,
      filters,
      companyStats,
      
      // Computed
      filteredModules,
      groupedModules,
      
      // Métodos
      refreshData,
      loadCompanyModules,
      toggleModulePermission,
      applyFilters,
      debouncedSearch,
      getCompanyStatusBadge,
      formatDate,
      applyTemplate,
      copyPermissionsFromCompany,
      saveBulkChanges
    }
  }
}
</script>

<style>
.permisos-modulos-container {
  padding: 1.5rem;
  background-color: var(--secondary-bg-main);
  color: var(--text-primary);
  min-height: calc(100vh - 4rem);
  max-width: 1400px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.header-content {
  flex: 1;
}

.page-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
}

.page-subtitle {
  margin: 0.5rem 0 0 0;
  color: var(--text-secondary);
  font-size: 1rem;
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.btn-primary-custom {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  border: none;
  color: var(--text-white);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn-primary-custom:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  color: var(--text-white);
}

.btn-primary-custom:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.5);
}

.btn-primary-custom:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
}

.btn-secondary-custom {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-secondary-custom:hover {
  background: var(--secondary-bg-light);
  color: var(--text-primary);
  transform: translateY(-1px);
}

.btn-secondary-custom:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(108, 117, 125, 0.5);
}

.btn-secondary-custom:active {
  transform: translateY(0);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Filters Section */
.filters-section {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
}

.company-selector {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.company-select, .form-select {
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.company-select:focus, .form-select:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
  background-color: var(--input-bg);
}

.filter-controls {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1rem;
  align-items: end;
}

.search-box {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 0.875rem;
  z-index: 2;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  background-color: var(--input-bg);
  color: var(--text-primary);
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

.search-input::placeholder {
  color: var(--input-placeholder);
}

/* Company Info Card */
.company-info-card {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
}

.company-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.company-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.25rem;
}

.company-badges {
  display: flex;
  gap: 0.5rem;
}

.company-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.stat-item {
  text-align: center;
  padding: 1rem;
  background: var(--secondary-bg-light);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.stat-number {
  display: block;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary-color);
}

.stat-label {
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
  margin-top: 0.25rem;
}

/* Loading and Error States */
.loading-container {
  text-align: center;
  padding: 3rem;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  color: var(--text-primary);
}

.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;
  opacity: 0.6;
}

.empty-state h3 {
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.875rem;
}

/* Modules Grid */
.modules-permissions-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.module-group-title {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.75rem;
}

.module-children {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;
  margin-top: 0.75rem;
}

.module-child {
  border-left-style: dashed;
  opacity: 0.98;
}

.module-permission-card {
  position: relative;
  background: var(--card-bg);
  border-radius: 12px;
  padding: 1.25rem;
  border: 1px solid var(--border-color);
  box-shadow: var(--card-shadow);
  transition: all 0.2s ease;
  border-left: 3px solid var(--border-color);
}

.module-permission-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-color: var(--primary-color);
}

.module-permission-card:focus-within {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.25);
}

.module-assigned {
  border-left-color: var(--success-color);
  background: var(--card-bg);
}

.module-core {
  border-left-color: var(--danger-color);
}

.module-updating {
  opacity: 0.6;
  pointer-events: none;
}

.module-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.module-info {
  flex: 1;
}

.module-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
}

.module-code {
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin: 0;
  font-family: 'Monaco', 'Courier New', monospace;
  background: var(--secondary-bg-light);
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.module-toggle {
  margin-left: 1rem;
}

.form-check-input {
  background-color: var(--input-bg);
  border-color: var(--border-color);
}

.form-check-input:checked {
  background-color: var(--success-color);
  border-color: var(--success-color);
}

.form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(var(--success-color-rgb), 0.25);
}

.form-check-label {
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
}

/* Badge overrides */
.badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
}

.badge.bg-info {
  background-color: var(--info-color) !important;
}

.badge.bg-success {
  background-color: var(--success-color) !important;
}

.badge.bg-danger {
  background-color: var(--danger-color) !important;
}

.badge.bg-warning {
  background-color: var(--warning-color) !important;
  color: var(--warning-text) !important;
}

.badge.bg-secondary {
  background-color: var(--text-secondary) !important;
}

.badge.bg-primary {
  background-color: var(--primary-color) !important;
}

/* Alert overrides */
.alert {
  background: var(--card-bg);
  border: 1px solid var(--danger-color);
  color: var(--text-primary);
  border-radius: 8px;
}

.alert-danger {
  background: var(--danger-bg);
  border-color: var(--danger-color);
  color: var(--danger-text);
}

.module-body {
  margin-bottom: 1rem;
}

.module-description {
  color: var(--text-secondary);
  margin-bottom: 1rem;
  line-height: 1.5;
  font-size: 0.875rem;
}

.module-badges {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.module-warning {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--warning-bg);
  border: 1px solid var(--warning-color);
  border-radius: 6px;
  color: var(--warning-text);
  font-size: 0.8rem;
}

.module-footer {
  border-top: 1px solid var(--border-color);
  padding-top: 1rem;
  font-size: 0.75rem;
  color: var(--text-secondary);
}

.module-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(var(--card-bg-rgb), 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
}

/* Modal Styles */
.modal-dialog {
  max-width: 600px;
}

.modal-content {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  color: var(--text-primary);
}

.modal-header {
  border-bottom: 1px solid var(--border-color);
  background: var(--secondary-bg-light);
}

.modal-title {
  color: var(--text-primary);
  font-weight: 600;
}

.modal-footer {
  border-top: 1px solid var(--border-color);
  background: var(--secondary-bg-light);
}

.bulk-assign-section {
  margin-bottom: 1.5rem;
}

.bulk-assign-section h6 {
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.template-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.template-buttons .btn {
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 992px) {
  .filters-section {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
  
  .filter-controls {
    grid-template-columns: 2fr 1fr 1fr;
  }
  
  .company-stats {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .permisos-modulos-container {
    padding: 1rem;
  }
  
  .page-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  
  .filters-section {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1rem;
  }
  
  .filter-controls {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .company-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  .modules-permissions-grid {
    grid-template-columns: 1fr;
  }
  
  .module-header {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .module-toggle {
    margin-left: 0;
    align-self: flex-start;
  }
  
  .template-buttons {
    flex-direction: column;
  }
  
  .company-info-card {
    padding: 1rem;
  }
}

@media (max-width: 576px) {
  .permisos-modulos-container {
    padding: 0.75rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .page-subtitle {
    font-size: 0.875rem;
  }
  
  .company-stats {
    grid-template-columns: 1fr;
  }
  
  .stat-number {
    font-size: 1.25rem;
  }
  
  .modules-permissions-grid {
    gap: 0.75rem;
  }
  
  .module-permission-card {
    padding: 1rem;
  }
  
  .filters-section {
    padding: 0.75rem;
  }
  
  .company-info-card {
    padding: 0.75rem;
  }
  
  .btn-primary-custom, .btn-secondary-custom {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }
  
  .header-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
}
/* Variables CSS locales para compatibilidad */
:root {
  --card-bg-rgb: 255, 255, 255;
  --success-color-rgb: 34, 197, 94;
  --border-color-rgb: 229, 231, 235;
  --primary-color-rgb: 59, 130, 246;
  
  /* Variables adicionales para consistencia */
  --info-color: #3b82f6;
  --success-color: #22c55e;
  --warning-color: #f59e0b;
  --warning-text: #92400e;
  --warning-bg: rgba(251, 191, 36, 0.1);
  --danger-color: #ef4444;
  --danger-text: #dc2626;
  --danger-bg: rgba(239, 68, 68, 0.1);
}

[data-theme="dark"] {
  --card-bg-rgb: 31, 41, 55;
  --success-color-rgb: 34, 197, 94;
  --border-color-rgb: 75, 85, 99;
  --primary-color-rgb: 59, 130, 246;
  
  --info-color: #60a5fa;
  --success-color: #4ade80;
  --warning-color: #fbbf24;
  --warning-text: #fcd34d;
  --warning-bg: rgba(251, 191, 36, 0.2);
  --danger-color: #f87171;
  --danger-text: #fca5a5;
  --danger-bg: rgba(239, 68, 68, 0.2);
}



/* Text utilities with theme support */
.text-muted {
  color: var(--text-secondary) !important;
}

.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Spinner with theme support */
.spinner-border {
  color: var(--primary-color);
  width: 1.5rem;
  height: 1.5rem;
}

.text-primary .spinner-border {
  color: var(--primary-color);
}

/* Content section animation */
.content-section {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Accesibilidad mejorada */
.btn-primary-custom:focus-visible,
.btn-secondary-custom:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

/* Focus states mejorados para tarjetas */
.module-permission-card:focus-within {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.25);
}

/* Transiciones suaves globales */
.permisos-modulos-container * {
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

/* Scrollbar styling */
.permisos-modulos-container::-webkit-scrollbar {
  width: 8px;
}

.permisos-modulos-container::-webkit-scrollbar-track {
  background: var(--secondary-bg-light);
}

.permisos-modulos-container::-webkit-scrollbar-thumb {
  background: var(--border-color);
  border-radius: 4px;
}

.permisos-modulos-container::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}
</style>
